import 'react-toastify/dist/ReactToastify.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useRef, useState, useMemo, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { OpenAPI } from './api';
import { setQueryClientInstance } from './api/core/request';
import { BrandedNavbar } from './app/components/BrandedNavbar';
import { Footer } from './app/components/Footer';
import { Session } from './app/components/Session';
import { FormSubmitContext } from './app/contexts/FormSubmitContext';
import { NavigationElementsContext } from './app/contexts/NavigationElementsContext';
import { ScrollToTop } from './utils/scroll';
import SentryBoundary from './utils/SentryBoundary';

OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'include';

export const App = () => {
  const footerRef = useRef<HTMLElement>(null);
  const navbarRef = useRef<HTMLElement>(null);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);
  const location = useLocation();

  const isOnboardingRoute = location.pathname.includes('onboarding');

  // Create query client inside component
  const queryClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: true,
          refetchOnReconnect: true,
          retry: false,
          retryDelay: 100000,
          staleTime: 5 * 60 * 1000, // 5 minutes
        },
      },
    });
    setQueryClientInstance(client);
    return client;
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      const active = document.activeElement as HTMLInputElement | null;
      if (active && active.type === 'number' && active === e.target) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <SentryBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="page-container">
          <ScrollToTop />
          <Session>
            <BrandedNavbar navbarRef={navbarRef} />
            <NavigationElementsContext.Provider value={{ footerRef, navbarRef }}>
              <FormSubmitContext.Provider value={{ isSubmitEnabled, setIsSubmitEnabled }}>
                <Outlet />
              </FormSubmitContext.Provider>
            </NavigationElementsContext.Provider>
          </Session>
          {!isOnboardingRoute && <Footer elementRef={footerRef} />}
          <ToastContainer
            position="top-center"
            autoClose={3000}
            closeOnClick
            pauseOnFocusLoss
            pauseOnHover
            draggable
          />
        </div>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SentryBoundary>
  );
};
